import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Type, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, TitleTooShortDetails } from "@/types/technicalAudit";

interface TitleTooShortCardProps {
	issue: PageIssue;
}

export function TitleTooShortCard({ issue }: TitleTooShortCardProps) {
	const details = issue.issue_details as TitleTooShortDetails;
	const percentage = Math.min((details.title_length / details.min_length) * 100, 100);

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Title Too Short</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						This SEO title tag is only {details.title_length} characters. Titles shorter than {details.min_length} characters miss keyword opportunities.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Type className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Current Title
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<blockquote className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-4 rounded-r-lg">
						<p className="text-sm font-medium text-amber-900 dark:text-amber-300">
							&ldquo;{details.current_title}&rdquo;
						</p>
					</blockquote>
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<span className={cn("text-2xl font-bold", "text-amber-700 dark:text-amber-400")}>
								{details.title_length}
							</span>
							<span className="text-sm text-muted-foreground">
								/ {details.min_length} minimum
							</span>
						</div>
						<div className="w-full h-3 bg-muted rounded-full overflow-hidden">
							<div
								className="h-full rounded-full transition-all bg-amber-500"
								style={{ width: `${percentage}%` }}
							/>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
